import { usePDFPrintHook } from "@careinfo/layout";
import { tw } from "@careinfo/tailwind";
import { useSessionStorage } from "@uidotdev/usehooks";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import APIS from "../../constant/EndPoint";
import { IFilterExpenseNote } from "../../types/interface";
import { hasValues } from "../../utils/hasValues";
import Card from "../Reusable/Card";
import DataTable from "../Reusable/DataTable";
import Filter from "./Filter";
import { toast } from "react-toastify";

const ExpenseNote = () => {
  const [localState, handleSetState] = useSessionStorage<IFilterExpenseNote>(
    "recieve-manifest-filter",
    {
      created__range: null,
      from_branch: null,
      ship_via: null,
    }
  );
  const [searchParams, setSearchParms] = useSearchParams();
  const [isFilterVisible, setFilterVisiable] = useState(hasValues(localState));
  const checkedAllItems = searchParams.get("selectAllItems") === "1";
  const handleIndividualSelect = (value: string) => {
    let selectedItems = searchParams.get("selectedItems")?.split(",");
    if (!selectedItems || selectedItems === undefined) {
      searchParams.set("selectedItems", value);
      setSearchParms(searchParams);
      return;
    }
    let itemIndex = selectedItems?.findIndex((item) => item === value);
    if (itemIndex > -1) {
      selectedItems.splice(itemIndex, 1);
      const newSelectedItems = selectedItems
        ?.map((item) => item)
        .join(",")
        .toString();
      if (newSelectedItems.length) {
        searchParams.set("selectedItems", newSelectedItems);
      } else {
        searchParams.delete("selectedItems");
      }
      searchParams.set("selectAllItems", "0");
      setSearchParms(searchParams);
    } else {
      selectedItems?.push(value);
      const newSelectedItems = selectedItems
        ?.map((item) => item)
        .join(",")
        .toString();
      searchParams.set("selectedItems", newSelectedItems);
      setSearchParms(searchParams);
    }
  };

  const isSelected = (value: string) => {
    const selectedItems = searchParams.get("selectedItems")?.split(",");
    if (!selectedItems) {
      return false;
    }
    return selectedItems.includes(value);
  };

  const handleSelectAll = (table: any) => {
    const {
      options: { data: tableData },
    } = table;
    const isSelected = searchParams.get("selectAllItems");
    if (isSelected === "1") {
      searchParams.set("selectAllItems", "0");
      searchParams.delete("selectedItems");
      setSearchParms(searchParams);
    } else {
      const selectedItems = tableData
        ?.map((item: any) => item.id)
        .join(",")
        .toString();
      searchParams.set("selectAllItems", "1");
      searchParams.set("selectedItems", selectedItems);
      setSearchParms(searchParams);
    }
  };

  const { setValue, getValues, watch, reset, control } =
    useForm<IFilterExpenseNote>({
      defaultValues: async () => {
        return {
          created__range: localState?.created__range ?? null,
          from_branch: localState?.from_branch ?? null,
          ship_via: localState?.ship_via ?? null,
        };
      },
    });

  const handleFilterReset = () => {
    reset({
      created__range: null,
      from_branch: null,
      ship_via: null,
    });
  };
  const navigate = useNavigate();
  let defaultColumns: any = [
    {
      id: "select",
      header: ({ table }: any) => {
        return (
          <input
            type="checkbox"
            checked={checkedAllItems}
            onChange={() => handleSelectAll(table)}
            className={tw`cursor-pointer`}
          />
        );
      },
      cell: ({ row }: any) => (
        <input
          type="checkbox"
          checked={isSelected(row?.original?.id)}
          onChange={() => handleIndividualSelect(row?.original?.id)}
          className={tw`cursor-pointer`}
        />
      ),
    },
    {
      header: "ID",
      accessorKey: "tracking_number",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p>{getValue()}</p>
          </>
        );
      },
    },
    {
      header: "Created",
      accessorKey: "created",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p>
              {getValue()
                ? moment(getValue()).format("YY-MM-DD HH:mm:ss")
                : "N/A"}
            </p>
          </>
        );
      },
    },
    {
      header: "Origin",
      accessorKey: "from_branch.name",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p className={tw`uppercase`}>{getValue()}</p>
          </>
        );
      },
    },
    {
      header: "Ship Via",
      accessorKey: "ship_via.name",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p className={tw`uppercase`}>{getValue()}</p>
          </>
        );
      },
    },
    {
      header: "Total Items",
      accessorKey: "total_items",
      cell: ({ getValue }: any) => {
        return (
          <>
            <p>{getValue() ?? "N/A"}</p>
          </>
        );
      },
    },
    {
      header: "Total Amount",
      accessorKey: "total_amount",
      cell: ({ getValue }: any) => {
        return (
          <>
            <p>{getValue() ?? "N/A"}</p>
          </>
        );
      },
    },
  ];

  const filterParams = watch();
  const handleNavigate = (row: any) => {
    navigate(`/edit/${row.original?.id}`);
  };

  useEffect(() => {
    handleSetState({
      ...localState,
      ...filterParams,
    });
  }, [filterParams]); //eslint-disable-line

  const params = useCallback(() => {
    const newValue = {};
    for (const [key, value] of Object.entries(filterParams)) {
      if (value) {
        if (Array.isArray(value)) {
          newValue[key] = value.map((v) => v.value).join(",");
        } else if (typeof value === "object") {
          newValue[key] = value.id;
        } else if (!value) {
          newValue[key] = null;
        } else {
          newValue[key] = value;
        }
      }
    }
    return newValue;
  }, [filterParams]);

  const { downloadPrint, isPrintLoading } = usePDFPrintHook();

  const handlePrint = async () => {
    const selectedOrder = searchParams.get("selectedItems")?.split(",");
    if (!selectedOrder) {
      toast.error("Please select at least one first");
      return;
    }
    const url = `${APIS.EXPENSE_NOTE}print/`;
    let formData = {
      ids: selectedOrder,
    };
    try {
      const res = await downloadPrint(url, formData);
    } catch (err) {
      console.error(err);
    }
  };
  const handlePrintSummary = async () => {
    const selectedOrder = searchParams.get("selectedItems")?.split(",");
    if (!selectedOrder) {
      toast.error("Please select at least one first");
      return;
    }
    const url = `${APIS.EXPENSE_NOTE}print/`;
    let formData = {
      ids: selectedOrder,
      type: "summary",
    };
    try {
      const res = await downloadPrint(url, formData);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <Card title="Filters" onClick={() => setFilterVisiable(!isFilterVisible)}>
        <Filter
          isFilterVisible={isFilterVisible}
          reset={handleFilterReset}
          control={control}
          setValue={setValue}
          getValues={getValues}
        />
      </Card>
      <DataTable
        url={APIS.EXPENSE_NOTE}
        queryKey={"expense-note"}
        columns={defaultColumns}
        tableName="expense-note"
        addNewPagePath={"/create"}
        handleDetailNavigate={handleNavigate}
        // tableName={"Consignments"}
        params={{
          ...params(),
        }}
        actionMenu={
          <>
            <div className={tw`flex gap-x-2 items-center`}>
              <button
                className={tw`px-2 border  rounded focus:outline-none border-primary `}
                disabled={isPrintLoading}
                onClick={() => handlePrintSummary()}
              >
                Summary Print
              </button>
              <button
                className={tw`px-2 border  rounded focus:outline-none border-primary `}
                disabled={isPrintLoading}
                onClick={() => handlePrint()}
              >
                Slip Print
              </button>
            </div>
          </>
        }
      />
    </>
  );
};

export default ExpenseNote;
